import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, Email as EmailIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { BusinessData } from '../../types/business';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import AnimatedLoader from '../../components/common/AnimatedLoader';
import { isValidGSTN, getStateCode } from '../../utils/gstHelpers';
import { awsUserService } from '../../services/aws-user-service';
import indexedDBService from '../../services/IndexedDBService';

// Step 1: Account Details Validation
const accountValidationSchema = Yup.object({
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password should be of minimum 8 characters length')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Confirm password is required'),
});

// Step 2: Business Details Validation
// Step 2: Business Details Validation
const businessDetailsValidationSchema = Yup.object({
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters'),
  entityType: Yup.string()
    .required('Entity type is required'),
  natureOfBusiness: Yup.string()
    .required('Nature of business is required')
    .oneOf(['manufacturing', 'services', 'trader', 'social_media'], 'Please select a valid business type'),
  commencementDate: Yup.date()
    .required('Commencement date is required')
    .max(new Date(), 'Commencement date cannot be in the future'),
  turnover: Yup.string()
    .required('Business turnover is required'),
  officeAddressLine1: Yup.string()
    .required('Office address is required')
    .min(5, 'Address must be at least 5 characters'),
  officeCity: Yup.string()
    .required('City is required')
    .min(2, 'City must be at least 2 characters'),
  officeState: Yup.string()
    .required('State is required'),
  officePincode: Yup.string()
    .required('Pincode is required')
    .matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
  authorizedRepName: Yup.string()
    .required('Authorized representative name is required')
    .min(2, 'Name must be at least 2 characters'),
  authorizedRepContact: Yup.string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Contact must be 10 digits'),
  numberOfUsers: Yup.string()
    .required('Please select number of users'),
  gstRegistered: Yup.string()
    .required('Please select GST registration status')
    .oneOf(['registered', 'not_registered'], 'Invalid GST registration status'),
  gstn: Yup.string()
    .when('gstRegistered', {
      is: 'registered',
      then: (schema) => schema
        .required('GSTN is required for registered businesses')
        .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Enter valid GSTN')
        .test('state-code-match', 'GSTN state code does not match selected state', function (value) {
          if (!value) return true;
          const { officeState } = this.parent;
          if (!officeState) return true;

          const expectedStateCode = getStateCode(officeState);
          if (!expectedStateCode) return true;

          return value.startsWith(expectedStateCode);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  additionalPlaces: Yup.array().when('hasAdditionalPlaces', {
    is: true,
    then: (schema) => schema.of(
      Yup.object().shape({
        addressLine1: Yup.string().required('Address is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        pincode: Yup.string().required('Pincode is required').matches(/^[0-9]{6}$/, 'Invalid pincode'),
        gstn: Yup.string()
          .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Enter valid GSTN')
          .test('additional-state-code', 'GSTN state code must match state', function (value) {
            if (!value) return true;
            const { state } = this.parent;
            if (!state) return true;
            const code = getStateCode(state);
            return code ? value.startsWith(code) : true;
          })
          .nullable()
      })
    ),
    otherwise: (schema) => schema.notRequired()
  })
});

// Define steps - Phase 1 + Phase 2
const allSteps = ['Account Details', 'Business Details'];

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const useSignupForm = (activeStep: number, onSubmit: (values: any) => void) => {
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      displayName: '',
      phone: '',
      businessName: '',
      businessType: '',
      businessActivityType: '',
      manufacturingActivities: [],
      tradingType: '',
      serviceType: '',
      socialMediaType: '',
      gstRegistered: 'not_registered',
      gstType: '',
      gstn: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      role: '',
      purpose: 'business',
      dateOfBirth: null,
      // Phase II: Business Details
      entityType: '',
      natureOfBusiness: '',
      commencementDate: null,
      turnover: '',
      officeAddressLine1: '',
      officeAddressLine2: '',
      officeCity: '',
      officeState: '',
      officePincode: '',
      hasAdditionalPlaces: false,
      numberOfAdditionalPlaces: 0,
      authorizedRepName: '',
      authorizedRepContact: '',
      numberOfUsers: '',
      additionalPlaces: [],
    },
    validationSchema: accountValidationSchema,
    onSubmit,
    validateOnChange: true
  });

  return formik;
};

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { signup, login, updateUserProfile, currentUser, isSigningUp, setSigningUp, confirmSignup, resendSignupConfirmation, logout } = auth;
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Clear any existing session when entering signup
  useEffect(() => {
    const clearSession = async () => {
      try {
        await logout();
      } catch (e) {
        console.log('Error clearing session:', e);
      }
    };
    clearSession();
  }, []);

  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [emailToVerify, setEmailToVerify] = useState('');
  const [pendingUser, setPendingUser] = useState<string>(''); // Stores the UUID username
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [steps, setSteps] = useState(allSteps);
  const [otp, setOtp] = useState('');
  const [emailExistsDialogOpen, setEmailExistsDialogOpen] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleFormSubmit = async (values: any) => {
    setIsSubmitting(true);
    setSignupError('');

    try {
      if (activeStep === 0) {
        // Step 1: Create account
        console.log('Starting signup process (Step 1: Account Creation)...');
        const displayName = `${values.firstName} ${values.lastName}`.trim();
        setEmailToVerify(values.email);

        const phase1Data: any = {
          displayName: displayName,
          firstName: values.firstName,
          lastName: values.lastName,
          gstRegistered: 'not_registered',
          role: 'user',
        };

        const user = await signup(values.email, values.password, displayName, phase1Data);

        console.log('Signup successful, awaiting confirmation!');
        console.log('User created with ID:', user.uid);

        setPendingUser(user.uid);
        setVerificationDialogOpen(true);
        setSigningUp(true);

      } else if (activeStep === 1) {
        // Step 2: Save business details to AWS AND IndexedDB
        console.log('Saving business details to AWS and IndexedDB...');

        const businessDetails = {
          businessName: values.businessName,
          entityType: values.entityType,
          natureOfBusiness: values.natureOfBusiness,
          commencementDate: values.commencementDate,
          turnover: values.turnover,
          gstRegistered: values.gstRegistered,
          gstn: values.gstRegistered === 'registered' ? values.gstn : null,
          officeAddress: {
            line1: values.officeAddressLine1,
            line2: values.officeAddressLine2 || '',
            city: values.officeCity,
            state: values.officeState,
            pincode: values.officePincode,
          },
          hasAdditionalPlaces: values.hasAdditionalPlaces,
          additionalPlaces: values.hasAdditionalPlaces ? values.additionalPlaces : [],
          authorizedRepresentative: {
            name: values.authorizedRepName,
            contact: values.authorizedRepContact,
          },
          numberOfUsers: values.numberOfUsers,
        };

        // Save to AWS DynamoDB
        try {
          await awsUserService.updateUserProfile(currentUser.uid, {
            businessDetails,
            setupCompleted: true,
          });
          console.log('Business details saved to AWS DynamoDB successfully');
        } catch (awsError) {
          console.error('Failed to save to AWS:', awsError);
          throw new Error('Failed to save business details. Please try again.');
        }

        // CRITICAL FIX: Also save to IndexedDB for local invoice creation
        try {
          const indexedDBBusinessData = {
            id: currentUser.uid,
            userId: currentUser.uid,
            uid: currentUser.uid,
            name: values.businessName,
            businessName: values.businessName,
            address: `${values.officeAddressLine1}${values.officeAddressLine2 ? ', ' + values.officeAddressLine2 : ''}, ${values.officeCity}, ${values.officeState} - ${values.officePincode}`,
            city: values.officeCity,
            state: values.officeState,
            pincode: values.officePincode,
            gstin: values.gstRegistered === 'registered' ? values.gstn : '',
            GSTIN: values.gstRegistered === 'registered' ? values.gstn : '',
            phone: values.authorizedRepContact,
            email: currentUser.email || '',
            entityType: values.entityType,
            natureOfBusiness: values.natureOfBusiness,
            turnover: values.turnover,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          await indexedDBService.saveBusinessDetails(indexedDBBusinessData);
          console.log('Business details saved to IndexedDB successfully');
        } catch (indexedDBError) {
          console.error('Failed to save to IndexedDB:', indexedDBError);
          // Don't throw error here - AWS save succeeded, IndexedDB is secondary
          console.warn('Continuing despite IndexedDB error - data is in AWS');
        }

        // Navigate to dashboard
        console.log('Signup complete! Redirecting to dashboard...');
        navigate('/dashboard');
      }

    } catch (error: any) {
      console.error('Signup error:', error);
      let errorMessage = 'Failed to sign up. Please try again.';

      if (error.code === 'auth/email-already-in-use' || error.message?.includes('UsernameExistsException')) {
        setExistingEmail(values.email);
        setEmailExistsDialogOpen(true);
        return;
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address format.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else {
        if (error.message) errorMessage = error.message;
      }

      setSignupError(errorMessage);
      setSigningUp(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useSignupForm(activeStep, handleFormSubmit);

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateCurrentStep = () => {
    if (activeStep === 0) {
      const hasFirstName = Boolean(formik.values.firstName);
      const hasLastName = Boolean(formik.values.lastName);
      const hasEmail = Boolean(formik.values.email);
      const hasPassword = Boolean(formik.values.password);
      const hasConfirmPassword = Boolean(formik.values.confirmPassword);
      const hasErrors = Boolean(
        formik.errors.firstName ||
        formik.errors.lastName ||
        formik.errors.email ||
        formik.errors.password ||
        formik.errors.confirmPassword
      );

      return hasFirstName && hasLastName && hasEmail && hasPassword && hasConfirmPassword && !hasErrors;
    } else if (activeStep === 1) {
      // Validate business details
      const hasBusinessName = Boolean(formik.values.businessName);
      const hasEntityType = Boolean(formik.values.entityType);
      const hasNatureOfBusiness = Boolean(formik.values.natureOfBusiness);
      const hasCommencementDate = Boolean(formik.values.commencementDate);
      const hasTurnover = Boolean(formik.values.turnover);
      const hasGstRegistered = Boolean(formik.values.gstRegistered);
      const hasOfficeAddress = Boolean(formik.values.officeAddressLine1);
      const hasOfficeCity = Boolean(formik.values.officeCity);
      const hasOfficeState = Boolean(formik.values.officeState);
      const hasOfficePincode = Boolean(formik.values.officePincode);
      const hasAuthorizedRepName = Boolean(formik.values.authorizedRepName);
      const hasAuthorizedRepContact = Boolean(formik.values.authorizedRepContact);
      const hasNumberOfUsers = Boolean(formik.values.numberOfUsers);

      // Check if GSTN is required and valid
      const gstValid = formik.values.gstRegistered === 'not_registered' ||
        (formik.values.gstRegistered === 'registered' && Boolean(formik.values.gstn));

      const hasErrors = Boolean(
        formik.errors.businessName ||
        formik.errors.entityType ||
        formik.errors.natureOfBusiness ||
        formik.errors.commencementDate ||
        formik.errors.turnover ||
        formik.errors.gstRegistered ||
        formik.errors.gstn ||
        formik.errors.officeAddressLine1 ||
        formik.errors.officeCity ||
        formik.errors.officeState ||
        formik.errors.officePincode ||
        formik.errors.authorizedRepName ||
        formik.errors.authorizedRepContact ||
        formik.errors.numberOfUsers
      );

      return hasBusinessName && hasEntityType && hasNatureOfBusiness &&
        hasCommencementDate && hasTurnover && hasGstRegistered && gstValid &&
        hasOfficeAddress && hasOfficeCity && hasOfficeState && hasOfficePincode &&
        hasAuthorizedRepName && hasAuthorizedRepContact && hasNumberOfUsers &&
        !hasErrors;
    }
    return false;
  };

  const checkEmailVerification = async () => {
    if (!otp) return;

    setIsSubmitting(true);
    setVerificationError('');

    try {
      console.log('Verifying code for user:', pendingUser || emailToVerify);
      await confirmSignup(pendingUser || emailToVerify, otp);
      setVerificationSuccess(true);

      // Auto-login after verification, then move to Step 2
      setTimeout(async () => {
        try {
          // Use UUID (pendingUser) for login to bypass Email Alias indexing lag
          const loginUser = pendingUser;
          const userProf = await login(loginUser, formik.values.password);

          // After successful login, close dialog
          setVerificationDialogOpen(false);
          
          if (userProf?.setupCompleted) {
            console.log('Setup already completed (invited user), redirecting to dashboard');
            navigate('/dashboard');
          } else {
            setActiveStep(1); // Move to business details step
            console.log('Login successful, moved to business details step');
          }
        } catch (loginErr) {
          console.error('Auto-login failed', loginErr);
          // Fallback: Just show "Please login" message if auto-login fails
          setVerificationSuccess(true);
          alert('Verification successful! Please log in with your credentials.');
          navigate('/login');
        }
      }, 1500);

    } catch (err: any) {
      console.error('Verification failed', err);
      let msg = err.message || 'Verification failed. Please check the code and try again.';

      if (err.code === 'ExpiredCodeException' || err.message?.includes('ExpiredCodeException')) {
        msg = 'The verification code has expired. Please click "Resend Code" to get a new one.';
        setOtp('');
      } else if (err.code === 'CodeMismatchException' || err.message?.includes('Invalid code') || err.message?.includes('CodeMismatchException')) {
        msg = 'The verification code is invalid. Please check the code and try again.';
      }

      setVerificationError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setVerificationError('');
      await resendSignupConfirmation(pendingUser || emailToVerify);
      setVerificationError('');
      alert('Verification code resent successfully to ' + emailToVerify);
    } catch (error: any) {
      console.error('Error resending verification code:', error);
      setVerificationError('Failed to resend verification code. Please try again.');
    }
  };

  const handleGoToLogin = () => {
    setEmailExistsDialogOpen(false);
    setSigningUp(false);
    navigate('/login');
  };

  const handleCloseEmailExistsDialog = () => {
    setEmailExistsDialogOpen(false);
    setExistingEmail('');
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName ? String(formik.errors.firstName) : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName ? String(formik.errors.lastName) : undefined}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              variant="outlined"
              margin="normal"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email ? String(formik.errors.email) : undefined}
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password ? String(formik.errors.password) : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword ? String(formik.errors.confirmPassword) : undefined}
            />
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Business Information
            </Typography>

            <TextField
              fullWidth
              id="businessName"
              name="businessName"
              label="Business Name"
              variant="outlined"
              margin="normal"
              value={formik.values.businessName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.businessName && Boolean(formik.errors.businessName)}
              helperText={formik.touched.businessName && formik.errors.businessName ? String(formik.errors.businessName) : undefined}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" error={formik.touched.entityType && Boolean(formik.errors.entityType)}>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    id="entityType"
                    name="entityType"
                    value={formik.values.entityType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Entity Type"
                  >
                    <MenuItem value="proprietorship">Proprietorship</MenuItem>
                    <MenuItem value="partnership">Partnership</MenuItem>
                    <MenuItem value="pvt_ltd">Private Limited (Pvt Ltd)</MenuItem>
                    <MenuItem value="public_ltd">Public Limited</MenuItem>
                    <MenuItem value="llp">Limited Liability Partnership (LLP)</MenuItem>
                    <MenuItem value="opc">One Person Company (OPC)</MenuItem>
                    <MenuItem value="section_8">Section 8 Company</MenuItem>
                    <MenuItem value="trust">Trust</MenuItem>
                    <MenuItem value="society">Society</MenuItem>
                    <MenuItem value="huf">Hindu Undivided Family (HUF)</MenuItem>
                  </Select>
                  {formik.touched.entityType && formik.errors.entityType && (
                    <FormHelperText>{String(formik.errors.entityType)}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" error={formik.touched.natureOfBusiness && Boolean(formik.errors.natureOfBusiness)}>
                  <InputLabel>Nature of Business</InputLabel>
                  <Select
                    id="natureOfBusiness"
                    name="natureOfBusiness"
                    value={formik.values.natureOfBusiness}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Nature of Business"
                  >
                    <MenuItem value="manufacturing">Manufacturing</MenuItem>
                    <MenuItem value="services">Services</MenuItem>
                    <MenuItem value="trader">Trader</MenuItem>
                    <MenuItem value="social_media">Social Media</MenuItem>
                  </Select>
                  {formik.touched.natureOfBusiness && formik.errors.natureOfBusiness && (
                    <FormHelperText>{String(formik.errors.natureOfBusiness)}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="commencementDate"
                  name="commencementDate"
                  label="Commencement of Business"
                  type="date"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.commencementDate || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.commencementDate && Boolean(formik.errors.commencementDate)}
                  helperText={formik.touched.commencementDate && formik.errors.commencementDate ? String(formik.errors.commencementDate) : undefined}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0],
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" error={formik.touched.turnover && Boolean(formik.errors.turnover)}>
                  <InputLabel>Business Turnover</InputLabel>
                  <Select
                    id="turnover"
                    name="turnover"
                    value={formik.values.turnover}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Business Turnover"
                  >
                    <MenuItem value="less_than_50_lakhs">Less than ₹50 Lakhs</MenuItem>
                    <MenuItem value="50_lakhs_to_10_crore">₹50 Lakhs to ₹10 Crore</MenuItem>
                    <MenuItem value="more_than_10_crore">More than ₹10 Crore</MenuItem>
                  </Select>
                  {formik.touched.turnover && formik.errors.turnover && (
                    <FormHelperText>{String(formik.errors.turnover)}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              GST Information
            </Typography>

            <FormControl fullWidth margin="normal" error={formik.touched.gstRegistered && Boolean(formik.errors.gstRegistered)}>
              <InputLabel>GST Registration Status</InputLabel>
              <Select
                id="gstRegistered"
                name="gstRegistered"
                value={formik.values.gstRegistered}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="GST Registration Status"
              >
                <MenuItem value="registered">Registered</MenuItem>
                <MenuItem value="not_registered">Not Registered</MenuItem>
              </Select>
              {formik.touched.gstRegistered && formik.errors.gstRegistered && (
                <FormHelperText>{String(formik.errors.gstRegistered)}</FormHelperText>
              )}
            </FormControl>

            {formik.values.gstRegistered === 'registered' && (
              <TextField
                fullWidth
                id="gstn"
                name="gstn"
                label="GSTN (GST Number)"
                variant="outlined"
                margin="normal"
                placeholder="e.g., 27AAPFU0939F1ZV"
                value={formik.values.gstn}
                onChange={(e) => {
                  formik.setFieldValue('gstn', e.target.value.toUpperCase());
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.gstn && Boolean(formik.errors.gstn)}
                helperText={formik.touched.gstn && formik.errors.gstn ? String(formik.errors.gstn) : undefined}
                inputProps={{ maxLength: 15 }}
              />
            )}

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Office Address
            </Typography>

            <TextField
              fullWidth
              id="officeAddressLine1"
              name="officeAddressLine1"
              label="Address Line 1"
              variant="outlined"
              margin="normal"
              value={formik.values.officeAddressLine1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.officeAddressLine1 && Boolean(formik.errors.officeAddressLine1)}
              helperText={formik.touched.officeAddressLine1 && formik.errors.officeAddressLine1 ? String(formik.errors.officeAddressLine1) : undefined}
            />

            <TextField
              fullWidth
              id="officeAddressLine2"
              name="officeAddressLine2"
              label="Address Line 2 (Optional)"
              variant="outlined"
              margin="normal"
              value={formik.values.officeAddressLine2}
              onChange={formik.handleChange}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="officeCity"
                  name="officeCity"
                  label="City"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.officeCity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.officeCity && Boolean(formik.errors.officeCity)}
                  helperText={formik.touched.officeCity && formik.errors.officeCity ? String(formik.errors.officeCity) : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={indianStates}
                  value={formik.values.officeState}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('officeState', newValue || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      margin="normal"
                      error={formik.touched.officeState && Boolean(formik.errors.officeState)}
                      helperText={formik.touched.officeState && formik.errors.officeState ? String(formik.errors.officeState) : undefined}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="officePincode"
                  name="officePincode"
                  label="Pincode"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.officePincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.officePincode && Boolean(formik.errors.officePincode)}
                  helperText={formik.touched.officePincode && formik.errors.officePincode ? String(formik.errors.officePincode) : undefined}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Authorized Representative
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="authorizedRepName"
                  name="authorizedRepName"
                  label="Name"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.authorizedRepName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.authorizedRepName && Boolean(formik.errors.authorizedRepName)}
                  helperText={formik.touched.authorizedRepName && formik.errors.authorizedRepName ? String(formik.errors.authorizedRepName) : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="authorizedRepContact"
                  name="authorizedRepContact"
                  label="Contact Number"
                  variant="outlined"
                  margin="normal"
                  value={formik.values.authorizedRepContact}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.authorizedRepContact && Boolean(formik.errors.authorizedRepContact)}
                  helperText={formik.touched.authorizedRepContact && formik.errors.authorizedRepContact ? String(formik.errors.authorizedRepContact) : undefined}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth margin="normal" error={formik.touched.numberOfUsers && Boolean(formik.errors.numberOfUsers)}>
              <InputLabel>Number of Users</InputLabel>
              <Select
                id="numberOfUsers"
                name="numberOfUsers"
                value={formik.values.numberOfUsers}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Number of Users"
              >
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
                <MenuItem value="5">5</MenuItem>
                <MenuItem value="more_than_5">More than 5</MenuItem>
              </Select>
              {formik.touched.numberOfUsers && formik.errors.numberOfUsers && (
                <FormHelperText>{String(formik.errors.numberOfUsers)}</FormHelperText>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  id="hasAdditionalPlaces"
                  name="hasAdditionalPlaces"
                  checked={formik.values.hasAdditionalPlaces}
                  onChange={(e) => {
                    formik.handleChange(e);
                    if (e.target.checked && formik.values.additionalPlaces.length === 0) {
                      formik.setFieldValue('additionalPlaces', [{ addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', gstn: '' }]);
                    } else if (!e.target.checked) {
                      formik.setFieldValue('additionalPlaces', []);
                    }
                  }}
                />
              }
              label="Has additional business places"
              sx={{ mt: 2 }}
            />

            {formik.values.hasAdditionalPlaces && (
              <Box sx={{ mt: 2 }}>
                {formik.values.additionalPlaces.map((place: any, index: number) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Additional Place #{index + 1}
                      </Typography>
                      {formik.values.additionalPlaces.length > 1 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const newPlaces = [...formik.values.additionalPlaces];
                            newPlaces.splice(index, 1);
                            formik.setFieldValue('additionalPlaces', newPlaces);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>

                    <TextField
                      fullWidth
                      label="Address Line 1"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      value={place.addressLine1}
                      onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].addressLine1`, e.target.value)}
                      error={formik.touched.additionalPlaces?.[index]?.addressLine1 && Boolean((formik.errors.additionalPlaces?.[index] as any)?.addressLine1)}
                      helperText={formik.touched.additionalPlaces?.[index]?.addressLine1 && (formik.errors.additionalPlaces?.[index] as any)?.addressLine1}
                    />

                    <TextField
                      fullWidth
                      label="Address Line 2 (Optional)"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      value={place.addressLine2}
                      onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].addressLine2`, e.target.value)}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="City"
                          variant="outlined"
                          size="small"
                          margin="dense"
                          value={place.city}
                          onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].city`, e.target.value)}
                          error={formik.touched.additionalPlaces?.[index]?.city && Boolean((formik.errors.additionalPlaces?.[index] as any)?.city)}
                          helperText={formik.touched.additionalPlaces?.[index]?.city && (formik.errors.additionalPlaces?.[index] as any)?.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={indianStates}
                          value={place.state}
                          onChange={(event, newValue) => {
                            formik.setFieldValue(`additionalPlaces[${index}].state`, newValue || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="State"
                              margin="dense"
                              size="small"
                              error={formik.touched.additionalPlaces?.[index]?.state && Boolean((formik.errors.additionalPlaces?.[index] as any)?.state)}
                              helperText={formik.touched.additionalPlaces?.[index]?.state && (formik.errors.additionalPlaces?.[index] as any)?.state}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pincode"
                          variant="outlined"
                          size="small"
                          margin="dense"
                          value={place.pincode}
                          onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].pincode`, e.target.value)}
                          error={formik.touched.additionalPlaces?.[index]?.pincode && Boolean((formik.errors.additionalPlaces?.[index] as any)?.pincode)}
                          helperText={formik.touched.additionalPlaces?.[index]?.pincode && (formik.errors.additionalPlaces?.[index] as any)?.pincode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="GSTN (Optional)"
                          variant="outlined"
                          size="small"
                          margin="dense"
                          placeholder={place.state ? `Must start with ${getStateCode(place.state) || '..'}` : ''}
                          value={place.gstn || ''}
                          onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].gstn`, e.target.value.toUpperCase())}
                          error={formik.touched.additionalPlaces?.[index]?.gstn && Boolean((formik.errors.additionalPlaces?.[index] as any)?.gstn)}
                          helperText={formik.touched.additionalPlaces?.[index]?.gstn && (formik.errors.additionalPlaces?.[index] as any)?.gstn}
                          inputProps={{ maxLength: 15 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newPlaces = [...formik.values.additionalPlaces, { addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', gstn: '' }];
                    formik.setFieldValue('additionalPlaces', newPlaces);
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Another Place
                </Button>
              </Box>
            )}
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  if (initialLoading) {
    return <AnimatedLoader fastLoad={true} duration={800} />;
  }

  if (currentUser && !isSigningUp) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Box>
      <Typography variant="h5" align="center" fontWeight="medium" gutterBottom mb={2}>
        Create Your Account
      </Typography>

      {signupError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {signupError}
        </Alert>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <form onSubmit={formik.handleSubmit}>
        {getStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            type="submit"
            disabled={isSubmitting || !validateCurrentStep()}
            sx={{
              py: 1,
              px: 3,
              fontWeight: 'medium',
              backgroundImage: 'var(--primary-gradient)',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeStep === steps.length - 1 ? (
              'Sign Up'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </form>

      <Box mt={3} textAlign="center">
        <Typography variant="body2">
          Already have an account?{' '}
          <Link
            component={RouterLink}
            to="/login"
            sx={{ textDecoration: 'none', fontWeight: 'medium' }}
          >
            Login
          </Link>
        </Typography>
      </Box>

      {/* Email Verification Dialog */}
      <Dialog open={verificationDialogOpen} disableEscapeKeyDown fullWidth maxWidth="sm" onClose={() => {
        if (verificationSuccess) return;
        setVerificationDialogOpen(false);
        setSigningUp(false);
      }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            {verificationSuccess ? 'Verification Successful' : 'Enter Verification Code'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {verificationSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Phase 1 of signup completed successfully!
              </Alert>
              <Typography variant="body1">
                Logging you in...
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We've sent a verification code to <strong>{emailToVerify}</strong>. Please enter it below to complete your registration.
              </Typography>

              <TextField
                autoFocus
                margin="dense"
                label="Confirmation Code"
                fullWidth
                variant="outlined"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                error={Boolean(verificationError)}
                helperText={verificationError}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={resendVerificationEmail}
                >
                  Resend Code
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {verificationSuccess ? (
            null
          ) : (
            <>
              <Button onClick={() => {
                setVerificationDialogOpen(false);
                setSigningUp(false);
              }}>Cancel</Button>
              <Button
                onClick={checkEmailVerification}
                variant="contained"
                disabled={otp.length < 1}
              >
                Verify
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Email Already Exists Dialog */}
      <Dialog open={emailExistsDialogOpen} onClose={handleCloseEmailExistsDialog}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="warning" />
            Email Already Registered
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The email <strong>{existingEmail}</strong> is already registered with an account.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Would you like to login to your existing account instead?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailExistsDialog}>
            Try Different Email
          </Button>
          <Button
            onClick={handleGoToLogin}
            variant="contained"
            sx={{
              backgroundImage: 'var(--primary-gradient)',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Signup;
